/**
 * Analytics event log — per-feature usage counter (opt-in via W4 flag).
 *
 * I2 (Wren v999999). Records that a family reached a surface, so Dan can
 * see which of the 65+ shipped features actually get used. Off by default
 * per-family via the `analytics_enabled` feature flag (W4). Nothing lands
 * in `usage_events` until a family opts in.
 *
 * Privacy contract:
 *   - Only family_id + event_key + timestamp. NEVER user_id, display_name,
 *     event properties, or payloads. Bucket count only.
 *   - Client-side buffered 30s flush so a chatty route doesn't spam.
 *   - Route paths are cardinality-normalized (UUIDs → `:id`, long numeric
 *     IDs → `:id`) so `route:/person/:id` collapses across children.
 *   - Off-by-default (HS17 spirit: user consent gate).
 *
 * Instrumentation:
 *   - `trackRouteChange(path)` — called from App.tsx on pathname change
 *   - `track(event_key)` — for feature-level instrumentation as it lands
 */

import { supabase } from './supabase'
import { getFlag } from './featureFlags'

const FLUSH_INTERVAL_MS = 30_000
const MAX_QUEUE = 200
const MAX_EVENT_KEY_LEN = 64
const ANALYTICS_FLAG_KEY = 'analytics_enabled'

interface QueuedEvent {
  ts: string
  event_key: string
}

let installed = false
let familyId: string | null = null
let enabled = false
let queue: QueuedEvent[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null
let lastRoutePath: string | null = null

function normalizeEventKey(key: string): string {
  return key.slice(0, MAX_EVENT_KEY_LEN)
}

function enqueue(eventKey: string): void {
  if (!enabled || !familyId) return
  const key = normalizeEventKey(eventKey)
  if (!key) return
  if (queue.length >= MAX_QUEUE) return
  queue.push({
    ts: new Date().toISOString(),
    event_key: key,
  })
}

async function flush(): Promise<void> {
  if (queue.length === 0 || !familyId || !enabled) return
  const batch = queue
  queue = []
  const rows = batch.map((e) => ({ ...e, family_id: familyId }))
  try {
    await supabase.from('usage_events').insert(rows)
  } catch {
    // Best-effort — never throw from analytics.
  }
}

/**
 * Install flush timer + pagehide handler. Idempotent; safe to call at boot
 * regardless of auth state (nothing enqueues until `enabled` becomes true).
 */
export function initAnalytics(): void {
  if (installed) return
  if (typeof window === 'undefined') return
  installed = true

  flushTimer = setInterval(() => {
    void flush()
  }, FLUSH_INTERVAL_MS)

  window.addEventListener('pagehide', () => {
    void flush()
  })
}

/**
 * Bind analytics to a family and load the opt-in gate. Fire-and-forget from
 * appStore — the fetch takes a few ms and events queued before it resolves
 * are dropped (safe; boot-time route change gets re-tracked as the user
 * navigates).
 */
export async function setAnalyticsFamily(id: string | null): Promise<void> {
  familyId = id
  lastRoutePath = null
  if (id == null) {
    queue = []
    enabled = false
    return
  }
  enabled = await getFlag(id, ANALYTICS_FLAG_KEY)
}

/**
 * Manual override for the opt-in gate — used by the useFeatureFlag realtime
 * subscription in App.tsx so a flag toggle from an admin dashboard propagates
 * without a page reload.
 */
export function setAnalyticsEnabled(on: boolean): void {
  enabled = on
  if (!on) queue = []
}

/** Record a feature-level event. No-op if flag disabled or family unset. */
export function track(eventKey: string): void {
  enqueue(eventKey)
}

/** Record a route change. De-duped against last path to avoid double-fires. */
export function trackRouteChange(path: string): void {
  const normalized = path
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/\d{4,}/g, '/:id')
  if (normalized === lastRoutePath) return
  lastRoutePath = normalized
  enqueue(`route:${normalized}`)
}

/** Test hook — force flush now. */
export async function flushAnalyticsForTest(): Promise<void> {
  await flush()
}

/** Tear down timers (tests + hot reload). */
export function teardownAnalytics(): void {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
  queue = []
  familyId = null
  enabled = false
  installed = false
  lastRoutePath = null
}
